import React, { useState, useEffect } from 'react';
import { getMyDocumentStatus, getDocumentDetail, signDocument, submitQuestionnaire } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { 
  FileText, 
  Shield,
  FileQuestion,
  FileCheck,
  Check,
  Clock,
  Eye
} from 'lucide-react';
import { toast } from 'sonner';

const DOC_TYPES = [
  { id: 'service_agreement', label: 'Service Agreement', icon: FileCheck, color: 'text-blue-400', bgColor: 'bg-blue-400/10' },
  { id: 'nda', label: 'NDA', icon: Shield, color: 'text-purple-400', bgColor: 'bg-purple-400/10' },
  { id: 'file_usage', label: 'File Usage', icon: FileText, color: 'text-green-400', bgColor: 'bg-green-400/10' },
  { id: 'questionnaire', label: 'Questionnaire', icon: FileQuestion, color: 'text-orange-400', bgColor: 'bg-orange-400/10' },
];

export default function ClientDocumentsPage() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [questionnaireDialogOpen, setQuestionnaireDialogOpen] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [docDetail, setDocDetail] = useState(null);
  const [questionnaireResponses, setQuestionnaireResponses] = useState({});
  const [signing, setSigning] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const response = await getMyDocumentStatus();
      setDocuments(response.data);
    } catch (error) {
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDocument = async (doc) => {
    setSelectedDoc(doc.document);
    try {
      const response = await getDocumentDetail(doc.document.id);
      setDocDetail(response.data);
      
      if (doc.document.doc_type === 'questionnaire' && doc.status === 'pending') {
        // Parse questions from content
        const questions = doc.document.content.split('\n').filter(q => q.trim());
        const initialResponses = {};
        questions.forEach((q, i) => {
          initialResponses[`q${i}`] = '';
        });
        setQuestionnaireResponses(initialResponses);
        setQuestionnaireDialogOpen(true);
      } else {
        setViewDialogOpen(true);
      }
    } catch (error) {
      toast.error('Failed to load document');
    }
  };

  const handleSign = async () => {
    if (!selectedDoc) return;
    setSigning(true);
    try {
      await signDocument(selectedDoc.id);
      toast.success('Document signed successfully');
      setViewDialogOpen(false);
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sign document');
    } finally {
      setSigning(false);
    }
  };

  const handleSubmitQuestionnaire = async (e) => {
    e.preventDefault();
    if (!selectedDoc) return;
    setSubmitting(true);
    try {
      await submitQuestionnaire(selectedDoc.id, questionnaireResponses);
      toast.success('Questionnaire submitted successfully');
      setQuestionnaireDialogOpen(false);
      fetchDocuments();
    } catch (error) {
      toast.error('Failed to submit questionnaire');
    } finally {
      setSubmitting(false);
    }
  };

  const getDocTypeInfo = (type) => DOC_TYPES.find(d => d.id === type) || DOC_TYPES[0];

  const getStatusBadge = (status) => {
    switch (status) {
      case 'signed':
      case 'submitted':
        return <Badge className="badge-approved"><Check className="w-3 h-3 mr-1" /> Completed</Badge>;
      case 'viewed':
        return <Badge className="badge-in_progress"><Eye className="w-3 h-3 mr-1" /> Viewed</Badge>;
      default:
        return <Badge className="badge-pending"><Clock className="w-3 h-3 mr-1" /> Pending</Badge>;
    }
  };

  const pendingDocs = documents.filter(d => d.status === 'pending' && d.document.requires_signature);
  const completedDocs = documents.filter(d => d.status !== 'pending' || !d.document.requires_signature);

  return (
    <div className="space-y-8" data-testid="client-documents-page">
      <div>
        <h1 className="text-3xl font-bold text-white mb-2">Documents</h1>
        <p className="text-slate-400">View and sign agreements, NDAs, and complete questionnaires</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      ) : documents.length === 0 ? (
        <Card className="bg-[#111827] border-white/10">
          <CardContent className="py-16">
            <div className="text-center">
              <FileText className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">No documents available</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Pending Documents */}
          {pendingDocs.length > 0 && (
            <div>
              <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-[#F59E0B]" />
                Action Required ({pendingDocs.length})
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {pendingDocs.map((item) => {
                  const typeInfo = getDocTypeInfo(item.document.doc_type);
                  const TypeIcon = typeInfo.icon;
                  return (
                    <Card 
                      key={item.document.id} 
                      className="bg-[#111827] border-[#F59E0B]/30 card-hover cursor-pointer"
                      onClick={() => handleViewDocument(item)}
                      data-testid={`pending-doc-${item.document.id}`}
                    >
                      <CardContent className="p-6">
                        <div className="flex items-start gap-4">
                          <div className={`w-12 h-12 rounded-lg ${typeInfo.bgColor} flex items-center justify-center ${typeInfo.color}`}>
                            <TypeIcon className="w-6 h-6" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-1">
                              <h3 className="text-lg font-bold text-white">{item.document.title}</h3>
                              {getStatusBadge(item.status)}
                            </div>
                            <p className="text-sm text-slate-400">{typeInfo.label}</p>
                            <Button 
                              size="sm" 
                              className="mt-3 bg-[#F59E0B] hover:bg-[#D97706] text-black"
                            >
                              {item.document.doc_type === 'questionnaire' ? 'Complete Now' : 'Review & Sign'}
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}

          {/* Completed/All Documents */}
          <div>
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#4A90D9]" />
              {pendingDocs.length > 0 ? 'Completed Documents' : 'All Documents'}
            </h2>
            <div className="space-y-3">
              {(pendingDocs.length > 0 ? completedDocs : documents).map((item) => {
                const typeInfo = getDocTypeInfo(item.document.doc_type);
                const TypeIcon = typeInfo.icon;
                return (
                  <Card 
                    key={item.document.id} 
                    className="bg-[#111827] border-white/10 card-hover cursor-pointer"
                    onClick={() => handleViewDocument(item)}
                    data-testid={`doc-${item.document.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-lg ${typeInfo.bgColor} flex items-center justify-center ${typeInfo.color}`}>
                            <TypeIcon className="w-5 h-5" />
                          </div>
                          <div>
                            <h3 className="text-white font-medium">{item.document.title}</h3>
                            <p className="text-sm text-slate-400">{typeInfo.label}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {getStatusBadge(item.status)}
                          {item.signed_at && (
                            <span className="text-xs text-slate-500">
                              {new Date(item.signed_at).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* View/Sign Document Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="bg-[#111827] border-white/10 max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">{selectedDoc?.title}</DialogTitle>
            <DialogDescription className="text-slate-400">
              {selectedDoc?.requires_signature 
                ? 'Please review and sign this document' 
                : 'Document for your reference'}
            </DialogDescription>
          </DialogHeader>
          <div className="contract-content text-sm text-slate-300 bg-[#0B1120] p-4 rounded whitespace-pre-wrap max-h-[400px] overflow-y-auto">
            {selectedDoc?.content}
          </div>
          {selectedDoc?.requires_signature && docDetail?.interaction?.status !== 'signed' && (
            <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
              <Button variant="ghost" onClick={() => setViewDialogOpen(false)} className="text-slate-400">
                Cancel
              </Button>
              <Button 
                onClick={handleSign}
                disabled={signing}
                className="bg-[#10B981] hover:bg-[#059669]"
                data-testid="sign-document-btn"
              >
                {signing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                    Signing...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    I Agree & Sign
                  </>
                )}
              </Button>
            </div>
          )}
          {docDetail?.interaction?.status === 'signed' && (
            <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
              <p className="text-green-400 flex items-center gap-2">
                <Check className="w-5 h-5" />
                You signed this document on {new Date(docDetail.interaction.signed_at).toLocaleString()}
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Questionnaire Dialog */}
      <Dialog open={questionnaireDialogOpen} onOpenChange={setQuestionnaireDialogOpen}>
        <DialogContent className="bg-[#111827] border-white/10 max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">{selectedDoc?.title}</DialogTitle>
            <DialogDescription className="text-slate-400">
              Please complete the following questionnaire
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitQuestionnaire} className="space-y-6">
            {selectedDoc?.content.split('\n').filter(q => q.trim()).map((question, index) => (
              <div key={index}>
                <Label className="text-slate-300 text-base">{question}</Label>
                <Textarea
                  value={questionnaireResponses[`q${index}`] || ''}
                  onChange={(e) => setQuestionnaireResponses({
                    ...questionnaireResponses,
                    [`q${index}`]: e.target.value
                  })}
                  placeholder="Your answer..."
                  className="mt-2 bg-[#1E293B] border-slate-700 text-white min-h-[80px]"
                  data-testid={`question-${index}`}
                />
              </div>
            ))}
            <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
              <Button type="button" variant="ghost" onClick={() => setQuestionnaireDialogOpen(false)} className="text-slate-400">
                Save for Later
              </Button>
              <Button 
                type="submit"
                disabled={submitting}
                className="bg-[#4A90D9] hover:bg-[#3A80C9]"
                data-testid="submit-questionnaire-btn"
              >
                {submitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                    Submitting...
                  </>
                ) : (
                  'Submit Responses'
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
